<i18n locale="en">
{
  "title": "Send test email",
  "labels": {
    "email": "Recipient email",
    "emailPlaceholder": "email{'@'}example.com"
  },
  "actions": {
    "send": "Send test",
    "cancel": "Cancel"
  },
  "success": "Test email sent successfully",
  "errors": {
    "template-not-found": "Template not found",
    "send-failed": "Failed to send test email",
    "unknown": "Something went wrong"
  },
  "validation": {
    "invalidEmail": "Please enter a valid email"
  }
}
</i18n>

<template>
  <OwnDialog v-model="isVisible" size="xs">
    <template #heading>
      <OwnDialogHeading :title="t('title')" />
    </template>

    <template #default>
      <OwnDialogContent>
        <OwnInputContainer :label="t('labels.email')" :errors="emailErrors">
          <OwnInput
            v-model="email"
            type="email"
            :placeholder="t('labels.emailPlaceholder')"
          />
        </OwnInputContainer>
      </OwnDialogContent>
    </template>

    <template #footer>
      <OwnDialogFooter>
        <OwnButton :text="t('actions.cancel')" @click="close" />
        <OwnButton
          :text="t('actions.send')"
          :processing="processing"
          :disabled="!isValid || !hasTemplate"
          variant="primary"
          @click="sendTest"
        />
      </OwnDialogFooter>
    </template>
  </OwnDialog>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useApplicationScope } from '@/core/store'
import { OlympusClient } from '@/OlympusClient'
import { OwnButton } from '@/ui/OwnButton'
import {
  OwnDialog,
  OwnDialogContent,
  OwnDialogFooter,
  OwnDialogHeading,
} from '@/ui/OwnDialog'
import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { useNotify } from '@/utils/useNotify'

interface Props {
  modelValue?: boolean
  templateId?: string
  templateSlug?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  templateId: undefined,
  templateSlug: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const { t } = useI18n()
const { activeBrandId } = useApplicationScope()
const notify = useNotify()

const email = ref('')
const processing = ref(false)

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const isValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value))

const emailErrors = computed(() => {
  if (email.value && !isValid.value) return [t('validation.invalidEmail')]
  return []
})

const close = () => {
  isVisible.value = false
}

const hasTemplate = computed(() => !!props.templateId || !!props.templateSlug)

const sendTest = async () => {
  if (!isValid.value || !hasTemplate.value) return

  processing.value = true
  try {
    await OlympusClient.marketing.v1.testSendEmail.testSendEmail({
      body: {
        brandId: activeBrandId.value,
        email: email.value,
        ...(props.templateId ? { templateId: props.templateId } : {}),
        ...(props.templateSlug ? { templateSlug: props.templateSlug } : {}),
      },
    })
    notify(t('success'), 'success')
    close()
  } catch (error) {
    if (OlympusClient.marketing.v1.testSendEmail.testSendEmail.isError(error)) {
      const key =
        error.code === 'template-not-found' || error.code === 'send-failed'
          ? error.code
          : 'unknown'
      notify(t(`errors.${key}`), 'error')
    } else {
      notify(t('errors.unknown'), 'error')
    }
  } finally {
    processing.value = false
  }
}

watch(
  () => props.modelValue,
  (visible) => {
    if (visible) {
      email.value = ''
      processing.value = false
    }
  }
)

defineExpose({ email, isValid, processing, sendTest })
</script>
